<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { feedbackUrl } from '../config/site'
import { useI18n } from '../composables/useI18n'

const { t } = useI18n()

defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const loading = ref(true)
const iframeHeight = ref(600)

// Restyle the ant-modal shell so the embedded iframe fills it edge-to-edge
// and the close button floats as a round button outside the top-right corner.
const modalWrapClass = [
  '[&_.ant-modal]:!max-w-[calc(100vw-24px)]',
  '[&_.ant-modal]:!pb-0',
  '[&_.ant-modal]:!pr-[52px]',
  '[&_.ant-modal-content]:!overflow-visible',
  '[&_.ant-modal-content]:!rounded-2xl',
  '[&_.ant-modal-content]:!bg-transparent',
  '[&_.ant-modal-content]:!p-0',
  '[&_.ant-modal-body]:!overflow-hidden',
  '[&_.ant-modal-body]:!rounded-2xl',
  '[&_.ant-modal-body]:!p-0',
  '[&_.ant-modal-body]:!leading-none',
  '[&_.ant-modal-close]:!top-0',
  '[&_.ant-modal-close]:!right-[-44px]',
  '[&_.ant-modal-close]:!z-10',
  '[&_.ant-modal-close]:!h-9',
  '[&_.ant-modal-close]:!w-9',
  '[&_.ant-modal-close]:!rounded-full',
  '[&_.ant-modal-close]:!bg-white',
  '[&_.ant-modal-close]:!text-gray-500',
  '[&_.ant-modal-close]:!shadow-lg',
  '[&_.ant-modal-close]:!transition-colors',
  '[&_.ant-modal-close:hover]:!bg-gray-100',
  '[&_.ant-modal-close:hover]:!text-gray-800',
  '[&_.ant-modal-close-x]:!flex',
  '[&_.ant-modal-close-x]:!h-9',
  '[&_.ant-modal-close-x]:!w-9',
  '[&_.ant-modal-close-x]:!items-center',
  '[&_.ant-modal-close-x]:!justify-center',
].join(' ')

function handleMessage(e: MessageEvent) {
  const type = e.data?.type
  if (type === 'FeedbackTicket:env') {
    e.source?.postMessage({ type: 'FeedbackTicket:env', data: {} }, { targetOrigin: '*' } as any)
  } else if (type === 'FeedbackTicket:log') {
    e.source?.postMessage(
      { type: 'FeedbackTicket:log', data: { logs: '', startTime: '', endTime: '' } },
      { targetOrigin: '*' } as any,
    )
  } else if (type === 'FeedbackTicket:resize' && typeof e.data.height === 'number') {
    const maxHeight = Math.max(window.innerHeight - 32, 360)
    iframeHeight.value = Math.min(e.data.height, maxHeight)
  }
}

onMounted(() => window.addEventListener('message', handleMessage))
onBeforeUnmount(() => window.removeEventListener('message', handleMessage))
</script>

<template>
  <a-modal
    :wrap-class-name="modalWrapClass"
    width="min(812px, 94vw)"
    :open="open"
    :footer="null"
    :keyboard="true"
    :mask-closable="true"
    @cancel="emit('update:open', false)"
  >
    <div class="relative rounded-2xl bg-white dark:bg-zinc-900">
      <iframe
        :src="feedbackUrl"
        class="block w-full min-h-[480px] max-h-[calc(100vh-32px)] overflow-hidden rounded-2xl border-0"
        :height="iframeHeight"
        allow="clipboard-write unload"
        scrolling="no"
        :title="t('shell.feedback')"
        @load="loading = false"
      />
      <div
        v-if="loading"
        class="absolute inset-0 flex items-center justify-center rounded-2xl bg-white dark:bg-zinc-900"
      >
        <a-spin size="large" />
      </div>
    </div>
  </a-modal>
</template>
